import { useEffect, useState } from "react";

interface StringListFieldProps {
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  label: string;
  placeholder?: string;
  helpText?: string;
}

/**
 * Edits a `string[]` as one entry per line.
 *
 * Puck's array field always produces objects keyed by its `arrayFields`, so a
 * plain list of strings can't be edited with one — declaring the array without
 * `arrayFields` gives rows that can be added but never opened. A textarea keeps
 * the stored shape a `string[]` and needs no migration of saved pages.
 *
 * The text is held locally so a blank line mid-typing survives; only non-empty
 * lines are published.
 */
const StringListField = ({
  value,
  onChange,
  label,
  placeholder,
  helpText,
}: StringListFieldProps) => {
  const published = (value ?? []).join("\n"),
    [text, setText] = useState(published);

  // Re-sync when the value changes elsewhere (undo, AI regeneration, switching
  // Between two components of the same type) but not while it round-trips our
  // Own edits, which would fight the cursor.
  useEffect(() => {
    setText((current) => {
      const currentLines = current
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (currentLines.join("\n") === published) {
        return current;
      }
      return published;
    });
  }, [published]);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <textarea
        value={text}
        rows={5}
        placeholder={placeholder}
        onChange={(event) => {
          setText(event.target.value);
          onChange(
            event.target.value
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean),
          );
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-info-foreground focus:border-info-foreground"
      />
      {helpText ? <p className="text-xs text-gray-500">{helpText}</p> : null}
    </div>
  );
};

export { StringListField };
